import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AssignedCampaign } from "../../types/campaign";
import { useTranslation } from "react-i18next";

interface CampaignMergeEditorProps {
  internalCampaigns: AssignedCampaign[];
  merges: { name: string; internalCampaignIds: string[] }[];
  onUpdateMerges: (
    newMerges: { name: string; internalCampaignIds: string[] }[]
  ) => void;
}

const CampaignMergeEditor: React.FC<CampaignMergeEditorProps> = ({
  internalCampaigns,
  merges,
  onUpdateMerges,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [newMergeName, setNewMergeName] = useState("");

  const handleCreateMerge = () => {
    if (!newMergeName.trim()) return;
    onUpdateMerges([
      ...merges,
      { name: newMergeName.trim(), internalCampaignIds: [] },
    ]);
    setNewMergeName("");
  };

  const handleAssignCampaign = (mergeIndex: number, campaignId: string) => {
    const newMerges = [...merges];
    const targetMerge = newMerges[mergeIndex];
    if (!targetMerge.internalCampaignIds.includes(campaignId)) {
      targetMerge.internalCampaignIds.push(campaignId);
      onUpdateMerges(newMerges);
    }
  };

  const handleRemoveCampaign = (mergeIndex: number, campaignId: string) => {
    const newMerges = [...merges];
    newMerges[mergeIndex].internalCampaignIds = newMerges[
      mergeIndex
    ].internalCampaignIds.filter((id) => id !== campaignId);
    onUpdateMerges(newMerges);
  };

  const handleRemoveMerge = (mergeIndex: number) => {
    const newMerges = [...merges];
    newMerges.splice(mergeIndex, 1);
    onUpdateMerges(newMerges);
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={2}>
        Campaign Groups (Merges)
      </Typography>

      <Box display="flex" gap={2} alignItems="center" mb={3}>
        <TextField
          label={t("campaigns.newGroupName")}
          value={newMergeName}
          onChange={(e) => setNewMergeName(e.target.value)}
          size="small"
        />
        <Button variant="contained" onClick={handleCreateMerge}>
          {t("campaigns.createGroup")}
        </Button>
      </Box>

      {merges.map((merge, index) => (
        <Box
          key={index}
          mb={3}
          p={2}
          border={`1px solid ${theme.palette.grey[300]}`}
          borderRadius={2}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontWeight={700}>{merge.name}</Typography>
            <IconButton onClick={() => handleRemoveMerge(index)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box mt={1} display="flex" gap={1} flexWrap="wrap">
            {merge.internalCampaignIds.map((id) => {
              const campaign = internalCampaigns.find((c) => c.id === id);
              return campaign ? (
                <Chip
                  key={id}
                  label={campaign.name}
                  onDelete={() => handleRemoveCampaign(index, id)}
                />
              ) : null;
            })}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" fontWeight={600} mb={1}>
            Assign Campaigns
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {internalCampaigns.map((campaign) => (
              <Button
                key={campaign.id}
                variant={
                  merge.internalCampaignIds.includes(campaign.id)
                    ? "outlined"
                    : "text"
                }
                onClick={() => handleAssignCampaign(index, campaign.id)}
                disabled={merge.internalCampaignIds.includes(campaign.id)}
                size="small"
              >
                {campaign.name}
              </Button>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default CampaignMergeEditor;
